import ValidationResult from "../libraries/classes/validation-result.class";
import requiredRule from "../libraries/validations/required";
import minRule from "../libraries/validations/min";
import maxRule from "../libraries/validations/max";
import isEmailRule from "../libraries/validations/is-email";
import sameAsRule from "../libraries/validations/same-as";
import customClosureRule from "../libraries/validations/custom-closure";
import isRegexPassed from "../libraries/validations/regex";
import isPhoneRule from "../libraries/validations/is-phone";
import isSsnRule from "../libraries/validations/is-ssn";
import { isCategorizedDiseases, needsOtherNote } from "./od-categorized-diseases";
import { t as systemTranslation } from "../utils/systemTranslations";

export default class Validation {
    rules = null
    valueContainer = null
    customClosures = {}

    /**
     * Controls that carry a rule of their OWN, not one the office configured: a "DISEASES with
     * Category and Checkbox Options" field refuses a ticked "Other" with no description (its text
     * is what Open Dental saves as Other Problems). Keyed by control name.
     * @type {Object<string, Object>}
     */
    intrinsic = {}

    /**
     * Language the patient is filling the form in ('en' | 'es' | 'ru'). Decides which
     * errorMessage variant (errorMessageEs / errorMessageRu) the messages are built from.
     * @type {string}
     */
    language = 'en'

    /**
     * Validation Result. Always create a new instance every time the validation is run
     * @type {ValidationResult}
     */
    validationResult = null

    /**
     * Create a new Validation handler
     * @param {Object} valueContainer
     * @param {Object} controls
     * @param {Object} definedClosures
     * @param {String} language
     */
    constructor(valueContainer, controls, definedClosures = {}, language = 'en') {
        this.valueContainer = valueContainer
        this.validationClosures = definedClosures
        this.language = language || 'en'
        this.setRules(controls)
    }

    /**
     * Set validation rules from the controls
     * @param {{validations: ValidationRule[]}} controls
     */
    setRules(controls) {
        const rules = {}

        // traversal all control and pick the validations info
        Object.entries(controls).forEach(controlInfo => {
            let [controlId, controlItem] = controlInfo
            let controlName = controlItem.name || controlId

            // no name => this field didn't have value
            if (!this.valueContainer.hasOwnProperty(controlName)) {
                return
            }

            rules[controlName] = controlItem.validations

            if (isCategorizedDiseases(controlItem)) {
                this.intrinsic[controlName] = controlItem
            }
        })

        this.rules = rules
    }

    /**
     * Start a validation check
     * @return {ValidationResult}
     */
    run() {
        this.validationResult = new ValidationResult()
        const controlKeys = Object.keys(this.rules)

        for (const key of controlKeys) {
            // pickup basic data
            const controlValue = this.valueContainer[key]
            const controlRules = this.rules[key] || []

            // no rule no run
            if (!controlRules.length) {
                continue;
            }

            /**
             * start the validation process by each rules added for the control
             */
            for (const validationRule of controlRules) {
                const status = this._singleRuleRun(validationRule, controlValue)
                if (!status) {
                    this.validationResult.addError(key, validationRule, this.language)
                }
            }
        }

        // Rules a control carries itself (no Control Configuration needed) — see `intrinsic`.
        // Hidden controls never get here: the renderer drops them before building this class.
        for (const key of Object.keys(this.intrinsic)) {
            if (needsOtherNote(this.intrinsic[key], this.valueContainer[key])) {
                this.validationResult.addError(key, {
                    ruleType: 'otherNote',
                    errorMessage: systemTranslation('describeOtherRequired', this.language)
                }, this.language)
            }
        }

        return this.validationResult
    }

    /**
     * Run single rule to check
     * @param {ValidationRule} validationRule
     * @param {any} fieldValue
     * @private
     */
    _singleRuleRun(validationRule, fieldValue) {
        const ruleType = (validationRule.ruleType || '').toLowerCase();
        switch (ruleType) {

            case "required":
                return requiredRule(fieldValue)

            case "min":
                return minRule(fieldValue, validationRule.additionalValue)

            case "max":
                return maxRule(fieldValue, validationRule.additionalValue)

            case "isemail":
                return isEmailRule(fieldValue)

            case "sameas":
                return sameAsRule(fieldValue, validationRule.additionalValue, this.valueContainer)

            case "customclosure":
                return customClosureRule(fieldValue, validationRule.additionalValue, this.valueContainer, this.customClosures)

            case "regex":
                return isRegexPassed(fieldValue, validationRule.additionalValue)

            case "isphone":
                return isPhoneRule(fieldValue)

            case "isssn":
                return isSsnRule(fieldValue)

            default:
                throw new TypeError(`This validation type ${validationRule.ruleType} is not supported.`);
        }
    }
}